import { BusinessObjectBase } from './base';
import type { Candidate } from './candidate';
import type { TrainingClass } from './training-class';

export interface ClassEnrollmentData {
  readonly id: number;
  readonly status?: string;
  readonly candidateId: number;
  readonly candidate?: Candidate;
  readonly trainingClassId: number;
  readonly trainingClass?: TrainingClass;
}

export interface ClassEnrollment extends Readonly<ClassEnrollmentData> {}
export class ClassEnrollment extends BusinessObjectBase {
  static readonly entityName = 'ClassEnrollment' as const;
  static readonly plural = 'class-enrollments';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ClassEnrollment[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ClassEnrollment(data));
  }

  static async findOne(filter?: any): Promise<ClassEnrollment | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ClassEnrollment(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<ClassEnrollment> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ClassEnrollment(data);
  }

  static async create(
    data: Omit<ClassEnrollmentData, 'id'>,
  ): Promise<ClassEnrollment> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ClassEnrollment(result);
  }

  static async updateById(
    id: number,
    data: Partial<ClassEnrollmentData>,
  ): Promise<ClassEnrollment> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new ClassEnrollment(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
